import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown, Scissors, Merge, Trash2, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { RecentlyDeleted, undoToast } from "./RecentlyDeleted";
import { SceneEditor } from "./SceneEditor";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const SceneRow = ({ s, idx, total, open, toggle, act, reload, checked, onCheck }) => (
  <div className={`card-s ${checked ? "border-red-500/50" : ""}`} data-testid={`scene-row-${s.number}`}>
    <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={toggle}>
      <Checkbox title="Select this scene (to delete several at once)" data-testid={`select-scene-${s.number}`} checked={checked} onClick={(e) => e.stopPropagation()} onCheckedChange={onCheck} />
      <span className="font-mono text-xs text-amber-400 w-14 shrink-0">SC {String(s.number).padStart(3, "0")}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{s.heading || "Untitled scene"}</div>
        <div className="text-xs text-slate-500 truncate">{s.dialogue.map((d) => `${d.speaker}: ${d.line}`).join("  ·  ") || s.action}</div>
      </div>
      <StatusBadge status={s.status} testId={`scene-status-${s.number}`} />
      {s.duration && <span className="font-mono text-[10px] text-slate-500 w-10 text-right shrink-0" title="Clip length" data-testid={`scene-length-${s.number}`}>{s.duration.toFixed(1)}s</span>}
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <span className="hidden md:contents">
          <IconBtn t={`move-up-${s.number}`} tip="Move up — make this scene play one place earlier" disabled={idx === 0} onClick={() => act("up")}><ArrowUp /></IconBtn>
          <IconBtn t={`move-down-${s.number}`} tip="Move down — make this scene play one place later" disabled={idx === total - 1} onClick={() => act("down")}><ArrowDown /></IconBtn>
          <IconBtn t={`split-scene-${s.number}`} tip="Split into two scenes — dialogue is divided between them (clears this scene's clip)" onClick={() => act("split")}><Scissors /></IconBtn>
          <IconBtn t={`merge-scene-${s.number}`} tip="Merge with the next scene into one (clears the clip)" disabled={idx === total - 1} onClick={() => act("merge")}><Merge /></IconBtn>
        </span>
        <IconBtn t={`delete-scene-${s.number}`} tip="Delete scene — you can undo for 10 minutes" onClick={() => act("delete")}><Trash2 /></IconBtn>
      </div>
      <span title={open ? "Close editor" : "Open to edit this scene"} data-testid={`scene-toggle-${s.number}`}>{open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}</span>
    </div>
    {open && <div className="px-3 pb-4 border-t border-[#2A3245]"><SceneEditor scene={s} onSaved={reload} /></div>}
  </div>
);

const IconBtn = ({ t, tip, children, ...p }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex">
        <button data-testid={t} aria-label={tip} {...p} className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-[#212738] disabled:opacity-30 disabled:pointer-events-none transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5">
          {children}
        </button>
      </span>
    </TooltipTrigger>
    <TooltipContent data-testid={`${t}-tooltip`} side="left" collisionPadding={8} className="max-w-[240px] text-xs pointer-events-none">{tip}{p.disabled ? " (not available here)" : ""}</TooltipContent>
  </Tooltip>
);

export const ScenesTab = ({ ep, scenes, reload, setTab }) => {
  const [open, setOpen] = useState(null);
  const act = async (s, idx, kind) => {
    try {
      if (kind === "up" || kind === "down") {
        const ids = scenes.map((x) => x.id);
        const j = kind === "up" ? idx - 1 : idx + 1;
        [ids[idx], ids[j]] = [ids[j], ids[idx]];
        await http.post(`/episodes/${ep.id}/scenes/reorder`, { ids });
      } else if (kind === "split") await http.post(`/scenes/${s.id}/split`);
      else if (kind === "merge") await http.post(`/scenes/${s.id}/merge-next`);
      else if (kind === "delete") {
        const warn = s.status === "approved" ? " It has an APPROVED clip that will be removed." : "";
        if (!window.confirm(`Delete scene ${s.number}?${warn} Later scenes are renumbered. You can undo for 10 minutes.`)) return;
        const { data } = await http.delete(`/scenes/${s.id}`);
        undoToast(ep.id, data.batch_id, `Scene ${s.number}`, reload);
      }
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => { await http.post(`/episodes/${ep.id}/scenes`); await reload(); };
  const [sel, setSel] = useState([]);
  useEffect(() => { setSel((x) => x.filter((id) => scenes.some((s) => s.id === id))); }, [scenes]);
  const toggleSel = (id, v) => setSel((x) => (v ? [...x, id] : x.filter((i) => i !== id)));
  const bulkDelete = async () => {
    const approvedN = scenes.filter((s) => sel.includes(s.id) && s.status === "approved").length;
    if (!window.confirm(`Delete ${sel.length} selected scene(s)?${approvedN ? ` ${approvedN} of them have approved clips.` : ""}`)) return;
    try {
      const { data } = await http.post(`/episodes/${ep.id}/scenes/delete`, { ids: sel });
      if (data.batch_id) undoToast(ep.id, data.batch_id, `${data.deleted} scene(s)`, reload);
      setSel([]);
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="max-w-5xl" data-testid="scenes-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="text-xl lg:text-2xl font-semibold">Scene list</h2>
        <span className="font-mono text-xs text-slate-500" data-testid="scenes-total-length">
          {scenes.length} scenes · {(() => { const est = scenes.some((s) => !s.duration); const t = Math.round(scenes.reduce((n, s) => n + (s.duration || 20), 0)); return `${est ? "~" : ""}${Math.floor(t / 60)}m ${String(t % 60).padStart(2, "0")}s`; })()}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <RecentlyDeleted ep={ep} reload={reload} />
          {scenes.length > 0 && (
            <Button data-testid="select-all-scenes-btn" size="sm" variant="ghost" onClick={() => setSel(sel.length === scenes.length ? [] : scenes.map((s) => s.id))} className="rounded-full">
              {sel.length === scenes.length ? "Clear selection" : "Select all"}
            </Button>
          )}
          {sel.length > 0 && (
            <Button data-testid="delete-selected-scenes-btn" size="sm" variant="destructive" onClick={bulkDelete} className="rounded-full">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete selected ({sel.length})
            </Button>
          )}
          <Button data-testid="add-scene-btn" size="sm" variant="secondary" onClick={add} className="rounded-full"><Plus className="w-3.5 h-3.5 mr-1.5" />Add scene</Button>
          <Button data-testid="go-to-cast-btn" size="sm" onClick={() => setTab("cast")} className="rounded-full"><Users className="w-3.5 h-3.5 mr-1.5" />Next: Cast</Button>
        </div>
      </div>
      {scenes.length === 0 && (
        <div className="panel p-8 text-sm text-slate-400" data-testid="scenes-empty">
          No scenes yet. Paste or write a script in the Script tab, then click "Split into 20s scenes" — or add scenes by hand.
        </div>
      )}
      <div className="space-y-2">
        {scenes.map((s, i) => (
          <SceneRow key={`${s.id}-${s.number}-${s.dialogue.length}-${s.heading}`} s={s} idx={i} total={scenes.length} open={open === s.id}
            toggle={() => setOpen(open === s.id ? null : s.id)} act={(k) => act(s, i, k)} reload={reload}
            checked={sel.includes(s.id)} onCheck={(v) => toggleSel(s.id, v)} />
        ))}
      </div>
    </div>
    </TooltipProvider>
  );
};
